import { InstrumentationBase, isWrapped } from '@opentelemetry/instrumentation';
import * as api from '@opentelemetry/api';

/*
 * Copyright The OpenTelemetry Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
var AttributeNames;
(function (AttributeNames) {
    AttributeNames["COMPONENT"] = "component";
    AttributeNames["EVENT_TYPE"] = "event_type";
    AttributeNames["TARGET_ELEMENT"] = "target_element";
    AttributeNames["TARGET_XPATH"] = "target_xpath";
    AttributeNames["VIEW_URL_ATTRIBUTE"] = "ums.rum.view.url";
    // NOT ON OFFICIAL SPEC
})(AttributeNames || (AttributeNames = {}));
const EVENT_NAVIGATION_NAME = 'Navigation';

class NavigationInstrumentation extends InstrumentationBase {
    constructor(config) {
        super('@heytap/cloud-observation-instrumentation-navigation', "2.1.1", config);
        this.config = config;
        this.component = 'navigation';
        this.moduleName = this.component;
        this._currentUrl = location.href;
        this.version = "2.1.1";
        this._isEnabled = false;
    }
    init() { }
    _createSpan(url, payload = {}) {
        const attributes = Object.assign({ [AttributeNames.VIEW_URL_ATTRIBUTE]: url }, payload);
        try {
            const span = this.tracer.startSpan('Navigation', {
                attributes,
            }, api.ROOT_CONTEXT);
            return span;
        }
        catch (e) {
            api.diag.error(this.component, e);
        }
        return undefined;
    }
    /**
     * Patches the history api
     */
    _patchHistoryApi() {
        this._unpatchHistoryApi();
        this._wrap(history, 'replaceState', this._patchHistoryMethod('replaceState'));
        this._wrap(history, 'pushState', this._patchHistoryMethod('pushState'));
        this._wrap(history, 'back', this._patchHistoryMethod('back'));
        this._wrap(history, 'forward', this._patchHistoryMethod('forward'));
        this._wrap(history, 'go', this._patchHistoryMethod('go'));
    }
    /**
     * Patches the certain history api method
     */
    _patchHistoryMethod(change_type) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const plugin = this;
        return (original) => {
            return function patchHistoryMethod(...args) {
                const urlBefore = location.href;
                const result = original.apply(this, args);
                const urlAfter = location.href;
                if (urlBefore !== urlAfter) {
                    plugin._recordView(urlAfter, {
                        'ums.rum.view.change_type': change_type,
                    });
                }
                return result;
            };
        };
    }
    _onPopstate() {
        const urlBefore = this._currentUrl;
        const urlAfter = location.href;
        if (urlBefore !== urlAfter) {
            this._recordView(urlAfter, {
                'ums.rum.view.change_type': 'popstate',
            });
        }
    }
    recordCustomView(url, payload = {}) {
        Object.entries(payload).forEach(([key, val]) => {
            typeof val === 'string' ? val : JSON.stringify(val);
        });
        return this._recordView(url, payload);
    }
    _recordView(url, payload = {}) {
        this._currentUrl = url;
        const span = this._createSpan(url, payload);
        span === null || span === void 0 ? void 0 : span.end();
    }
    /**
     * unpatch the history api methods
     */
    _unpatchHistoryApi() {
        if (isWrapped(history.replaceState))
            this._unwrap(history, 'replaceState');
        if (isWrapped(history.pushState))
            this._unwrap(history, 'pushState');
        if (isWrapped(history.back))
            this._unwrap(history, 'back');
        if (isWrapped(history.forward))
            this._unwrap(history, 'forward');
        if (isWrapped(history.go))
            this._unwrap(history, 'go');
    }
    /**
     * implements enable function
     */
    enable() {
        this._onPopstate = this._onPopstate.bind(this);
        const { trackViewsManually = false } = this.config;
        if (this._isEnabled)
            return;
        this._isEnabled = true;
        api.diag.debug('applying patch to', this.moduleName, this.version);
        if (trackViewsManually)
            return;
        window.addEventListener('popstate', this._onPopstate);
        this._patchHistoryApi();
    }
    /**
     * implements unpatch function
     */
    disable() {
        if (!this._isEnabled)
            return;
        this._isEnabled = false;
        api.diag.debug('removing patch from', this.moduleName, this.version);
        window.removeEventListener('popstate', this._onPopstate);
        this._unpatchHistoryApi();
    }
}

export { AttributeNames, EVENT_NAVIGATION_NAME, NavigationInstrumentation };
